import { useQuery } from '@tanstack/react-query';
import { useDashboard } from '@/hooks/useDashboardContext';
import { fooddb, OUTLET_TO_FOODDB, type AggregatorProvider } from '@/lib/supabase/fooddb';
import { slotsFor, type MetricConcept } from '@/lib/aggregator-metrics';
import {
  computeMetric,
  effectivePeriodRange,
  getCompareRange,
  toISTDateString,
  type DateRange,
  type Metric,
  type PeriodKey,
} from '@/lib/periods';

interface Args {
  outletId: string | 'all';
  period: PeriodKey;
  providers?: AggregatorProvider[];
}

export interface FunnelStage {
  key: 'impressions' | 'menu_opens' | 'carts_built' | 'orders_placed';
  label: string;
  metric: Metric;
  /** conversion from previous stage (0-100), current period */
  rateFromPrevious?: number;
}

interface Result {
  stages: FunnelStage[];
  current: DateRange;
  previous: DateRange;
}

const STAGE_CONCEPT: Record<FunnelStage['key'], MetricConcept> = {
  impressions: 'funnel.impressions',
  menu_opens: 'funnel.menu_opens',
  carts_built: 'funnel.carts_built',
  orders_placed: 'funnel.orders_placed',
};
const STAGE_LABEL: Record<FunnelStage['key'], string> = {
  impressions: 'Impressions',
  menu_opens: 'Menu opens',
  carts_built: 'Carts built',
  orders_placed: 'Orders placed',
};
const ORDER: FunnelStage['key'][] = ['impressions', 'menu_opens', 'carts_built', 'orders_placed'];

async function sumConcept(args: {
  concept: MetricConcept;
  providers: AggregatorProvider[];
  outletId: string | 'all';
  range: DateRange;
}): Promise<number> {
  const start = toISTDateString(args.range.start);
  const end = toISTDateString(new Date(args.range.end.getTime() - 1));

  // Each provider may have one or more (category, metric) slots that map to
  // this concept. Fire them in parallel and sum.
  const queries: Promise<number>[] = [];
  for (const provider of args.providers) {
    for (const slot of slotsFor(provider, args.concept)) {
      let q = fooddb
        .from('flax_metrics_daily')
        .select('value')
        .eq('provider', provider)
        .eq('category', slot.category)
        .eq('metric', slot.metric)
        .gte('date', start)
        .lte('date', end);
      if (args.outletId !== 'all') {
        const slug = OUTLET_TO_FOODDB[args.outletId];
        if (!slug) {
          queries.push(Promise.resolve(0));
          continue;
        }
        q = q.eq('outlet_id', slug);
      }
      queries.push(
        (async () => {
          const { data, error } = await q;
          if (error) throw error;
          const rows = (data ?? []) as unknown as Array<{ value: number | string }>;
          let s = 0;
          for (const r of rows) s += Number(r.value ?? 0);
          return s;
        })(),
      );
    }
  }
  const parts = await Promise.all(queries);
  return parts.reduce((a, b) => a + b, 0);
}

export function useAggregatorFunnel({
  outletId,
  period,
  providers = ['swiggy', 'zomato'],
}: Args) {
  const { customRange } = useDashboard();
  const current = effectivePeriodRange(period, customRange);
  const previous = getCompareRange(current, 'previous');

  return useQuery<Result>({
    queryKey: [
      'agg-funnel',
      outletId,
      providers.join(','),
      current.start.toISOString(),
    ],
    queryFn: async () => {
      const curValues = await Promise.all(
        ORDER.map((k) =>
          sumConcept({ concept: STAGE_CONCEPT[k], providers, outletId, range: current }),
        ),
      );
      const prevValues = await Promise.all(
        ORDER.map((k) =>
          sumConcept({ concept: STAGE_CONCEPT[k], providers, outletId, range: previous }),
        ),
      );

      const stages: FunnelStage[] = ORDER.map((k, i) => {
        const cur = curValues[i] ?? 0;
        const prev = prevValues[i] ?? 0;
        const above = i === 0 ? null : curValues[i - 1];
        return {
          key: k,
          label: STAGE_LABEL[k],
          metric: computeMetric(cur, prev),
          rateFromPrevious: above && above > 0 ? (cur / above) * 100 : undefined,
        };
      });

      return { stages, current, previous };
    },
    staleTime: 60_000,
  });
}
